/* eslint-disable import/prefer-default-export */
import axios from 'axios';
import type { IEtaiement, ArgsGetEtaiements, INote } from '~/types/etaiements';
import type { IPhase } from '~/types/estimates';
import type { IMetaPagination } from '~/types/global';

const getEtaiements = async (
  params: ArgsGetEtaiements,
): Promise<{ etaiements: IEtaiement[]; meta?: IMetaPagination } | null> => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements`,
    {
      headers: {
        Authorization: token,
      },
      params,
    },
  );
  return {
    etaiements: data.etaiements || [],
    meta: data.meta,
  };
};

const getEtaiementsList = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/list`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data?.list;
};

const getEtaiement = async (id: string) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/${id}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data.etaiement;
};

const createEtaiement = async (data: Partial<IEtaiement>): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/etaiements`,
    data,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return response.data;
};

const updateEtaiement = async (data: Partial<IEtaiement>): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { _id, ...updatedData } = data;
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}/etaiements/${_id}`,
    updatedData,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const updateEtaiementTransport = async ({
  etaiementId,
}: {
  etaiementId: string,
}) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/transport`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data;
};

const duplicateEtaiementPhase = async ({
  etaiementId,
  phaseId,
}: {
  etaiementId: string,
  phaseId: string
}): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/phases/duplicate/${phaseId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data;
};

const createEtaiementPhase = async (
  data: { etaiementId: string },
): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/etaiements/${data.etaiementId}/phases`,
    {},
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const reorderEtaiementPhases = async ({
  etaiementId,
  phaseIds,
}: {
  etaiementId: string;
  phaseIds: string[];
}): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/phases/reorder`,
    { phaseIds },
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const updateEtaiementPhase = async (data: Partial<IPhase> & { etaiementId: string })
: Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { _id, etaiementId, ...updatedData } = data;
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/phases/${_id}`,
    updatedData,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const deleteEtaiementPhase = async ({
  etaiementId,
  phaseId,
}: {
  etaiementId: string,
  phaseId: string
})
: Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.delete(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/phases/${phaseId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const deleteEtaiement = async ({
  etaiementId,
}: {
  etaiementId: string
}): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.delete(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const sendEtaiement = async (id: string) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/send/${id}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data;
};

const createEtaiementVersion = async (id: string) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/create-version/${id}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data;
};

const duplicateEtaiement = async ({
  etaiementId,
  clientId,
}: {
  etaiementId: string,
  clientId: string
}) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/duplicate/${etaiementId}/${clientId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );
  return data;
};

const addEtaiementNote = async (data: Partial<INote> & { documentId: string })
: Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { documentId, ...updatedData } = data;
  const response = await axios.post(
    `${process.env.REACT_APP_API_URL}/etaiements/${documentId}/notes`,
    updatedData,
    {
      headers: {
        Authorization: token,
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data;
};

const updateEtaiementNote = async (data: Partial<INote> & { documentId: string })
: Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { documentId, _id, ...updatedData } = data;
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}/etaiements/${documentId}/notes/${_id}`,
    updatedData,
    {
      headers: {
        Authorization: token,
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data;
};

const deleteEtaiementNote = async ({
  noteId,
  etaiementId,
}: {
  noteId: string, etaiementId: string
}): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.delete(
    `${process.env.REACT_APP_API_URL}/etaiements/${etaiementId}/notes/${noteId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

const exportEtaiement = async (
  id: string,
  filename: string | null,
  previewWindow: Window | null = null,
) => {
  const token = localStorage.getItem('token');
  if (!token) return null;

  const { data } = await axios.get(
    `${process.env.REACT_APP_API_URL}/etaiements/export/${id}`,
    {
      headers: {
        Authorization: token,
      },
      responseType: 'blob',
    },
  );

  if (data.type === 'application/json') {
    const message = await data.text();
    throw new Error(message || 'PDF generation failed');
  }

  const pdfBlob = data.type === 'application/pdf'
    ? data
    : new Blob([data], { type: 'application/pdf' });

  const fileURL = URL.createObjectURL(pdfBlob);
  const targetWindow = previewWindow;
  if (filename) {
    const link = document.createElement('a');
    link.href = fileURL;
    link.download = filename;
    link.click();
  } else if (targetWindow && !targetWindow.closed) {
    targetWindow.location.href = fileURL;
  } else {
    window.open(fileURL, '_blank');
  }
  setTimeout(() => URL.revokeObjectURL(fileURL), 60_000);
  return pdfBlob;
};

const addEtaiementFile = async (data: { _id: string, file: File })
: Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const { _id, file } = data;
  const response = await axios.put(
    `${process.env.REACT_APP_API_URL}/etaiements/file/${_id}`,
    { file },
    {
      headers: {
        Authorization: token,
        'Content-Type': 'multipart/form-data',
      },
    },
  );

  return response.data;
};

const deleteEtaiementFile = async ({
  fileId,
  etaiementId,
}: {
  fileId: string,
  etaiementId: string,
}): Promise<{ etaiement: IEtaiement }> => {
  const token = localStorage.getItem('token');
  const response = await axios.delete(
    `${process.env.REACT_APP_API_URL}/etaiements/file/${etaiementId}/${fileId}`,
    {
      headers: {
        Authorization: token,
      },
    },
  );

  return response.data;
};

export {
  getEtaiement,
  getEtaiements,
  getEtaiementsList,
  createEtaiement,
  updateEtaiement,
  updateEtaiementTransport,
  createEtaiementPhase,
  reorderEtaiementPhases,
  updateEtaiementPhase,
  deleteEtaiementPhase,
  duplicateEtaiementPhase,
  deleteEtaiement,
  sendEtaiement,
  duplicateEtaiement,
  createEtaiementVersion,
  addEtaiementNote,
  updateEtaiementNote,
  deleteEtaiementNote,
  addEtaiementFile,
  deleteEtaiementFile,
  exportEtaiement,
};
